/**
 * User Workshop Assignment Modal
 * Admin modal for assigning users to workshops with granular permissions
 */

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Users, Check, Save, AlertCircle } from "lucide-react";
import { Workshop } from "../../types/workshop";
import { User } from "../../types";
import { useSnackbar } from "notistack";
import api from "../../services/api";
import { useTheme } from "../../contexts/ThemeContext";

interface UserWorkshopAssignmentModalProps {
  workshop: Workshop;
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
}

interface UserWithPermissions extends User {
  hasAccess?: boolean;
  otherWorkshop?: { id: number; name: string } | null;
  permissions?: {
    can_view: boolean;
    can_create: boolean;
    can_edit: boolean;
    can_delete: boolean;
  };
}

const UserWorkshopAssignmentModal: React.FC<
  UserWorkshopAssignmentModalProps
> = ({ workshop, isOpen, onClose, onSave }) => {
  const [users, setUsers] = useState<UserWithPermissions[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [assignmentMap, setAssignmentMap] = useState<Map<number, any>>(
    new Map()
  );
  const { enqueueSnackbar } = useSnackbar();
  const { isDarkMode } = useTheme();

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen, workshop.id]);

  const loadUsers = async () => {
    try {
      setLoading(true);

      // Get all users (API service already returns response.data)
      const allUsers: User[] = await api.get("/users/");

      // Filter out admin users - they have automatic access to all workshops
      const nonAdminUsers = allUsers.filter((user) => !user.is_admin);

      // Get users already assigned to this workshop
      const workshopUsers: any[] = await api.get(
        `/workshops/${workshop.id}/users`
      );

      console.log("Non-admin users:", nonAdminUsers);
      console.log("Workshop users:", workshopUsers);

      // Ensure workshopUsers is an array
      const safeWorkshopUsers = Array.isArray(workshopUsers)
        ? workshopUsers
        : [];

      // Create a map of workshop user assignments
      const newAssignmentMap = new Map(
        safeWorkshopUsers.map((wu: any) => [
          wu.user_id,
          {
            hasAccess: true,
            permissions: {
              can_view: wu.can_view ?? true,
              can_create: wu.can_create ?? false,
              can_edit: wu.can_edit ?? false,
              can_delete: wu.can_delete ?? false,
            },
          },
        ])
      );
      setAssignmentMap(newAssignmentMap);

      // Merge data - also check if users have other workshops assigned
      const usersWithPermissions: UserWithPermissions[] = await Promise.all(
        (Array.isArray(nonAdminUsers) ? nonAdminUsers : []).map(
          async (user) => {
            const assignment: any = newAssignmentMap.get(user.id);

            // Check if user has other workshops
            let otherWorkshop = null;
            if (!assignment) {
              try {
                const userWorkshops: any[] = await api.get(
                  `/users/${user.id}/workshops`
                );
                if (userWorkshops.length > 0) {
                  const firstWorkshop = userWorkshops[0];
                  otherWorkshop = {
                    id: firstWorkshop.workshop_id,
                    name: firstWorkshop.workshop.name,
                  };
                }
              } catch (err) {
                // Ignore errors
              }
            }

            return {
              ...user,
              hasAccess: !!assignment,
              otherWorkshop,
              permissions: assignment?.permissions || {
                can_view: true,
                can_create: false,
                can_edit: false,
                can_delete: false,
              },
            };
          }
        )
      );

      setUsers(usersWithPermissions);
    } catch (error: any) {
      console.error("Error loading users:", error);
      enqueueSnackbar(error.response?.data?.detail || "Error loading users", {
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAccess = (userId: number) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId
          ? {
              ...u,
              hasAccess: !u.hasAccess,
              permissions: !u.hasAccess
                ? {
                    can_view: true,
                    can_create: false,
                    can_edit: false,
                    can_delete: false,
                  }
                : undefined,
            }
          : u
      )
    );
  };

  const handleTogglePermission = (
    userId: number,
    permission: keyof UserWithPermissions["permissions"]
  ) => {
    setUsers((prev) =>
      prev.map((u) =>
        u.id === userId && u.permissions
          ? {
              ...u,
              permissions: {
                ...u.permissions,
                [permission]: !u.permissions[permission],
              },
            }
          : u
      )
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);

      // Prepare batch operations
      const operations = users.map(async (user) => {
        const assignment: any = assignmentMap.get(user.id);
        const hadAccess = !!assignment;

        if (user.hasAccess && !hadAccess) {
          // Check if user has other workshops assigned
          try {
            const userWorkshops: any[] = await api.get(
              `/users/${user.id}/workshops`
            );

            if (userWorkshops.length > 0) {
              // User already has a workshop, use reassign endpoint
              await api.put(
                `/users/${user.id}/workshops/reassign/${workshop.id}`,
                user.permissions
              );
            } else {
              // New assignment
              await api.post(
                `/users/${user.id}/workshops/${workshop.id}`,
                user.permissions
              );
            }
          } catch (err) {
            // If can't get user workshops, try direct assignment
            await api.post(
              `/users/${user.id}/workshops/${workshop.id}`,
              user.permissions
            );
          }
        } else if (!user.hasAccess && hadAccess) {
          // Remove workshop assignment
          await api.delete(`/users/${user.id}/workshops/${workshop.id}`);
        } else if (user.hasAccess && hadAccess) {
          // Update permissions if changed
          const permissionsChanged =
            JSON.stringify(user.permissions) !==
            JSON.stringify(assignment.permissions);
          if (permissionsChanged) {
            await api.put(
              `/users/${user.id}/workshops/${workshop.id}/permissions`,
              user.permissions
            );
          }
        }
      });

      await Promise.all(operations);

      enqueueSnackbar("User assignments saved successfully", {
        variant: "success",
      });
      onSave();
      onClose();
    } catch (error: any) {
      console.error("Error saving assignments:", error);
      enqueueSnackbar(
        error.response?.data?.detail || "Error saving assignments",
        { variant: "error" }
      );
    } finally {
      setSaving(false);
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const modalClass = `relative w-full max-w-4xl rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col ${
    isDarkMode ? "bg-gray-800" : "bg-white"
  }`;
  const infoBannerClass = `rounded-lg p-4 border ${
    isDarkMode ? "bg-blue-900/20 border-blue-800" : "bg-blue-50 border-blue-200"
  }`;
  const infoIconClass = `w-5 h-5 flex-shrink-0 mt-0.5 ${
    isDarkMode ? "text-blue-400" : "text-blue-600"
  }`;
  const infoTextClass = `text-sm ${
    isDarkMode ? "text-blue-200" : "text-blue-800"
  }`;
  const searchSectionClass = `p-6 pb-0 border-b ${
    isDarkMode ? "border-gray-700" : "border-gray-200"
  }`;
  const searchInputClass = `w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDarkMode
      ? "border-gray-600 bg-gray-700 text-white placeholder-gray-500"
      : "border-gray-300 bg-white text-gray-900 placeholder-gray-400"
  }`;
  const loadingSpinnerClass = `animate-spin rounded-full h-12 w-12 border-b-2 ${
    isDarkMode ? "border-blue-400" : "border-blue-600"
  }`;
  const noUsersIconClass = `w-16 h-16 mx-auto mb-4 ${
    isDarkMode ? "text-gray-600" : "text-gray-300"
  }`;
  const noUsersTextClass = isDarkMode ? "text-gray-400" : "text-gray-600";
  const footerClass = `px-6 py-4 border-t flex justify-between items-center ${
    isDarkMode ? "bg-gray-900 border-gray-700" : "bg-gray-50 border-gray-200"
  }`;
  const footerTextClass = `text-sm ${
    isDarkMode ? "text-gray-400" : "text-gray-600"
  }`;
  const cancelButtonClass = `px-4 py-2 border rounded-lg transition-colors ${
    isDarkMode
      ? "border-gray-600 text-gray-300 hover:bg-gray-700"
      : "border-gray-300 text-gray-700 hover:bg-gray-100"
  }`;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className={modalClass}
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-5 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Users className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">User Assignments</h2>
                    <p className="text-blue-100 text-sm mt-1">
                      {workshop.name} - Manage user access and permissions
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Info Banner */}
            <div className="px-6 pt-6">
              <div className={infoBannerClass}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={infoIconClass} />
                  <div className={infoTextClass}>
                    <p className="font-semibold mb-1">
                      Single Workshop Assignment
                    </p>
                    <p>
                      Each user can only be assigned to ONE workshop at a time.
                      If you assign a user who already has another workshop,
                      their previous assignment will be replaced.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Search */}
            <div className={searchSectionClass}>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users by name, username, or email..."
                className={searchInputClass}
              />
            </div>

            {/* Users List */}
            <div className="flex-1 overflow-y-auto p-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className={loadingSpinnerClass}></div>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredUsers.map((user) => {
                    const userCardClass = `border-2 rounded-xl p-4 transition-all ${
                      user.hasAccess
                        ? isDarkMode
                          ? "border-blue-800 bg-blue-900/20"
                          : "border-blue-200 bg-blue-50"
                        : isDarkMode
                        ? "border-gray-700 bg-gray-800"
                        : "border-gray-200 bg-white"
                    }`;
                    const toggleTrackClass = `w-11 h-6 ${
                      isDarkMode ? "bg-gray-700" : "bg-gray-200"
                    } peer-focus:outline-none peer-focus:ring-4 ${
                      isDarkMode
                        ? "peer-focus:ring-blue-800"
                        : "peer-focus:ring-blue-300"
                    } rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600`;
                    const userNameClass = `font-semibold ${
                      isDarkMode ? "text-white" : "text-gray-900"
                    }`;
                    const adminBadgeClass = `px-2 py-0.5 text-xs font-medium rounded-full ${
                      isDarkMode
                        ? "bg-purple-900/30 text-purple-400"
                        : "bg-purple-100 text-purple-700"
                    }`;
                    const otherWorkshopBadgeClass = `px-2 py-0.5 text-xs font-medium rounded-full ${
                      isDarkMode
                        ? "bg-orange-900/30 text-orange-400"
                        : "bg-orange-100 text-orange-700"
                    }`;
                    const usernameClass = `text-sm ${
                      isDarkMode ? "text-gray-400" : "text-gray-600"
                    }`;
                    const otherWorkshopTextClass = `text-xs mt-1 ${
                      isDarkMode ? "text-orange-400" : "text-orange-600"
                    }`;
                    const permissionsSectionClass = `mt-4 pt-4 border-t ${
                      isDarkMode ? "border-blue-800" : "border-blue-200"
                    }`;
                    const permissionsTitleClass = `text-xs font-medium mb-3 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`;
                    const permissionCheckboxClass = isDarkMode
                      ? "w-4 h-4 text-blue-600 border-gray-600 rounded focus:ring-blue-600 focus:ring-offset-gray-800 bg-gray-700"
                      : "w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-offset-white";
                    const permissionTextClass = isDarkMode
                      ? "text-sm text-gray-300 group-hover:text-blue-400"
                      : "text-sm text-gray-700 group-hover:text-blue-600";
                    return (
                      <div key={user.id} className={userCardClass}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="mt-1">
                              <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={user.hasAccess}
                                  onChange={() => handleToggleAccess(user.id)}
                                  className="sr-only peer"
                                />
                                <div className={toggleTrackClass}></div>
                              </label>
                            </div>

                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className={userNameClass}>
                                  {user.full_name || user.username}
                                </h4>
                                {user.is_admin && (
                                  <span className={adminBadgeClass}>Admin</span>
                                )}
                                {user.otherWorkshop && !user.hasAccess && (
                                  <span
                                    className={otherWorkshopBadgeClass}
                                    title={`Assigned to: ${user.otherWorkshop.name}`}
                                  >
                                    Other Workshop
                                  </span>
                                )}
                              </div>
                              <p className={usernameClass}>@{user.username}</p>
                              <p className="text-sm text-gray-500">
                                {user.email}
                              </p>
                              {user.otherWorkshop && !user.hasAccess && (
                                <p className={otherWorkshopTextClass}>
                                  Currently assigned to:{" "}
                                  <span className="font-semibold">
                                    {user.otherWorkshop.name}
                                  </span>
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {user.hasAccess && user.permissions && (
                          <div className={permissionsSectionClass}>
                            <p className={permissionsTitleClass}>PERMISSIONS</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                              {Object.entries({
                                can_view: "View",
                                can_create: "Create",
                                can_edit: "Edit",
                                can_delete: "Delete",
                              }).map(([key, label]) => (
                                <label
                                  key={key}
                                  className="flex items-center gap-2 cursor-pointer group"
                                >
                                  <input
                                    type="checkbox"
                                    checked={
                                      user.permissions![
                                        key as keyof typeof user.permissions
                                      ]
                                    }
                                    onChange={() =>
                                      handleTogglePermission(
                                        user.id,
                                        key as keyof UserWithPermissions["permissions"]
                                      )
                                    }
                                    className={permissionCheckboxClass}
                                  />
                                  <span className={permissionTextClass}>
                                    {label}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {!loading && filteredUsers.length === 0 && (
                <div className="text-center py-12">
                  <Users className={noUsersIconClass} />
                  <p className={noUsersTextClass}>No users found</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={footerClass}>
              <p className={footerTextClass}>
                {users.filter((u) => u.hasAccess).length} user(s) with access
              </p>
              <div className="flex gap-3">
                <button onClick={onClose} className={cancelButtonClass}>
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg
                           hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default UserWorkshopAssignmentModal;
